import {
  isAnyRealEstateCategory,
  isLongTermRentalsCategory,
  isShortTermRentalsCategory,
} from '@kijiji/category'
import { type StandardListingAttributes, ResultsView } from '@kijiji/generated/graphql-types'
import { Fragment } from 'react'

import { HomeFeedListContainer } from '@/components/homepage/homepage-feed/styled'
import { ClientRender } from '@/components/shared/client-render'
import {
  type CoreListingCardData,
  CoreListingCard,
} from '@/components/shared/core-listing-card/CoreListingCard'
import { RenderListingCard } from '@/components/srp/search-list/RenderListingCard'
import { RenderRichCard } from '@/components/srp/search-list/RenderRichCard'
import { SearchGridContainer, SearchListContainer } from '@/components/srp/search-list/styled'
import { InlineAdSlot } from '@/components-page/srp/advertisement/InlineAdSlot'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { trackEventListingClick } from '@/domain/srp/tracking/trackEventListingClick'
import { shouldAllowGridView } from '@/features/search/utils/shouldAllowGridView'
import { useGetSearchResultsData } from '@/hooks/srp'
import { useLocale } from '@/hooks/useLocale'
import { type Listing } from '@/types/search'

type RenderListingCardsProps = {
  listings: Listing[]
  topAds?: Listing[]
  currentView?: ResultsView
}

export const RenderListingCards: React.FC<RenderListingCardsProps> = ({
  listings,
  topAds = [],
  currentView,
}) => {
  const { data } = useGetSearchResultsData()
  const { searchQuery } = data || {}
  const { apiLocale } = useLocale()

  const category = getSearchCategoryFromSearchQuery(searchQuery?.category, apiLocale)
  const shouldOverrideRealEstateTitle =
    isLongTermRentalsCategory(category.id) || isShortTermRentalsCategory(category.id)

  if (
    currentView === ResultsView.Grid &&
    (isAnyRealEstateCategory(category.id) || shouldAllowGridView(category.id))
  ) {
    const combinedListings: Listing[] = [...topAds, ...listings]
    return (
      <SearchGridContainer data-testid="srp-grid-search-list" as="ul">
        {combinedListings.map((item, index) => {
          const handleTrackListingClick = () => {
            trackEventListingClick({
              searchView: currentView,
              position: index + 1,
              listing: item,
            })
          }

          return (
            <Fragment key={item.id}>
              <RenderRichCard
                handleClick={handleTrackListingClick}
                key={item.id}
                index={index}
                listing={item}
                shouldOverrideRealEstateTitle={shouldOverrideRealEstateTitle}
              />

              <ClientRender>
                <InlineAdSlot index={index} totalListings={listings.length} isGridView />
              </ClientRender>
            </Fragment>
          )
        })}
      </SearchGridContainer>
    )
  }

  if (currentView === ResultsView.Grid) {
    const combinedListings: Listing[] = [...topAds, ...listings]

    return (
      <HomeFeedListContainer data-testid="srp-grid-search-list" isSRPGridView>
        {combinedListings.map((item, index) => {
          const listing: CoreListingCardData = {
            categoryId: category.id,
            id: item.id,
            imageUrls: item.imageUrls,
            isPlaceholder: false,
            location: {
              id: item.location.id,
              name: item.location.name,
            },
            title: item.title,
            url: item.url,
            price: item.price,
            attributes: item.attributes as StandardListingAttributes | undefined,
          }

          const handleTrackListingClick = (listingPosition: number) => {
            trackEventListingClick({
              searchView: currentView,
              position: listingPosition,
              listing: item,
            })
          }

          return (
            <Fragment key={item.id}>
              <CoreListingCard
                index={index}
                listing={listing}
                allowFavourite
                key={item.id}
                isTopAd={item.flags.topAd ?? false}
                isSRPGridView={true}
                onClick={handleTrackListingClick}
              />

              <ClientRender>
                <InlineAdSlot index={index} totalListings={listings.length} isGridView />
              </ClientRender>
            </Fragment>
          )
        })}
      </HomeFeedListContainer>
    )
  }

  return (
    <SearchListContainer data-testid="srp-search-list">
      {listings.map((item, index) => (
        <RenderListingCard
          key={item.id}
          item={item}
          index={index}
          isMapCard={currentView === ResultsView.Map}
        />
      ))}
    </SearchListContainer>
  )
}
